import { useStateParams } from "../hooks/useStateParams";
import { ApiGigInviteDto } from "../models/api/gigInvite";
import { GigInviteDto } from "../models/app/gigInvite";
import { mapFromApiGigInvite } from "../models/mappers/gigInvite";
import { GigApiPaginatedData } from "./common/apiResponse";
import { GigApiFetcherResponse, useAuthenticatedGigApiFetcher } from "./common/fetching";
import { useGiggedApiSWR } from "./common/giggedApiSWR";
import { ITEMS_PER_PAGE } from "./common/pagination";

export type ApiDeclineGigInviteDto = {
    reason: string;
}

export type UseTalentGigInvitesReturn = {
    gigInvites: GigInviteDto[];
    isLoading: boolean;
    pageIndex: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
    setPageIndex: (pageIndex: number) => void;
    setPageSize: (pageSize: number) => void;
    acceptGigInvite: (inviteId: string) => Promise<GigApiFetcherResponse<void>>;
    acceptGigChanges: (inviteId: string) => Promise<GigApiFetcherResponse<void>>;
    declineGigInvite: (inviteId: string, dto: ApiDeclineGigInviteDto) => Promise<GigApiFetcherResponse<void>>;
    declineGigChanges: (inviteId: string) => Promise<GigApiFetcherResponse<void>>;
    isAcceptingInvite: boolean;
    isDecliningInvite: boolean;
    isAcceptingChanges: boolean;
    isDecliningChanges: boolean;
};

export const useTalentGigInvites = (statusId?: number): UseTalentGigInvitesReturn => {
    const [pageIndex, setPageIndex] = useStateParams<number>(0, "pageIndex", "number");
    const [pageSize, setPageSize] = useStateParams<number>(ITEMS_PER_PAGE, "pageSize", "number");

    const url = "api/me/gig-invites?" +
        `pageIndex=${pageIndex}` +
        `&pageSize=${pageSize}` +
        (statusId !== undefined ? `&statusId=${statusId}` : "");

    const { data, isLoading, mutate } = useGiggedApiSWR<GigApiPaginatedData<ApiGigInviteDto>>(url);

    const [acceptGigInvite, isAcceptingInvite] = useAuthenticatedGigApiFetcher<void, void, { inviteId: string }>("PUT", ({ inviteId }) => ({
        url: `api/me/gigs/invites/${inviteId}/actions/accept`
    }));
    const [declineGigInvite, isDecliningInvite] = useAuthenticatedGigApiFetcher<ApiDeclineGigInviteDto, void, { inviteId: string, dto: ApiDeclineGigInviteDto }>("PUT", ({ inviteId, dto }) => ({
        url: `api/me/gigs/invites/${inviteId}/actions/decline`,
        body: dto
    }));
    const [acceptGigChanges, isAcceptingChanges] = useAuthenticatedGigApiFetcher<void, void, { inviteId: string }>("PUT", ({ inviteId }) => ({
        url: `api/me/gigs/invites/${inviteId}/actions/accept-changes`
    }));
    const [declineGigChanges, isDecliningChanges] = useAuthenticatedGigApiFetcher<void, void, { inviteId: string }>("PUT", ({ inviteId }) => ({
        url: `api/me/gigs/invites/${inviteId}/actions/decline-changes`
    }));

    const gigInvites = data?.items ? data.items.map(mapFromApiGigInvite) : [];

    return {
        gigInvites,
        isLoading,
        pageIndex: data?.pageIndex ?? 0,
        pageSize: data?.pageSize ?? pageSize,
        totalCount: data?.totalCount ?? 0,
        totalPages: data?.totalPages ?? 0,
        hasPreviousPage: data?.hasPreviousPage ?? false,
        hasNextPage: data?.hasNextPage ?? false,
        setPageIndex,
        setPageSize,
        acceptGigInvite: async (inviteId: string) => {
            const response = await acceptGigInvite({ inviteId });
            if (response.success) {
                await mutate();
            }
            return response;
        },
        acceptGigChanges: async (inviteId: string) => {
            const response = await acceptGigChanges({ inviteId });
            if (response.success) {
                await mutate();
            }
            return response;
        },
        declineGigInvite: async (inviteId: string, dto: ApiDeclineGigInviteDto) => {
            const response = await declineGigInvite({ inviteId, dto });
            if (response.success) {
                await mutate();
            }
            return response;
        },
        declineGigChanges: async (inviteId: string) => {
            const response = await declineGigChanges({ inviteId });
            if (response.success) {
                await mutate();
            }
            return response;
        },
        isAcceptingInvite,
        isDecliningInvite,
        isAcceptingChanges,
        isDecliningChanges
    };
};
